import type { CustomQueryPageParams } from '@luna/api';
import { request } from '@luna/api';
import { getEnv } from '@luna/env';
import { deepClean } from '@luna/utils';
import { createQueryString } from '@luna/router';
import type { AccountFilter } from '@api-client/accounts';

import type {
    Account,
    AccountsFilterOptions,
    FilterablePagedListResponse,
} from '../types/aggregationService';
import { getAdditionalHeaders } from '../accountEntityService';

type AdditionalData = AccountFilter & { unfilteredItemsCount?: number };

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 50;

export const fetchAccount = (id: string): Promise<Account> => {
    const { apiGatewayUrl } = getEnv();

    return request<Account>(`${apiGatewayUrl}product/public/accounts/${id}`);
};

export const updateAccount = (payload: Partial<Account>): Promise<Account> => {
    const { apiGatewayUrl } = getEnv();

    return request.put(`${apiGatewayUrl}product/public/accounts/${payload.id}`, payload);
};

export const fetchAccountListPage: (
    {
        pageNumber,
        pageSize,
        embed,
        isPrivate,
        ...rest
    }: CustomQueryPageParams<AccountsFilterOptions>,
    { signal }: { signal?: AbortSignal },
) => Promise<{
    pageCount: number;
    pageNumber: number;
    data: Account[];
    pageSize: number;
    additionalData: AdditionalData;
    totalElements: number;
}> = async (
    {
        pageNumber = DEFAULT_PAGE_NUMBER,
        pageSize = DEFAULT_PAGE_SIZE,
        embed,
        isPrivate,
        ...rest
    }: CustomQueryPageParams<AccountsFilterOptions>,
    { signal } = {},
) => {
    const areFiltersEmbedded = embed === 'filter';
    const fetchEmbeddedFilterValues = pageNumber === DEFAULT_PAGE_NUMBER && areFiltersEmbedded;

    const queryParams = {
        page: pageNumber,
        size: pageSize,
        ...(fetchEmbeddedFilterValues && { embed }),
        ...deepClean(rest),
    };

    const response = await request<FilterablePagedListResponse<Account, AccountFilter>>(
        `${getEnv().apiGatewayUrl}product/public/accounts${createQueryString(queryParams)}`,
        {
            signal,
            headers: getAdditionalHeaders(isPrivate),
        },
    );

    const filter = response?._embedded?.filter ?? {};
    const unfilteredItemsCount = response?.unfilteredItemsCount;
    const additionalData = {
        ...filter,
        ...(unfilteredItemsCount && { unfilteredItemsCount }),
    };

    return {
        data: response.items,
        additionalData,
        pageNumber: response.page.number,
        pageCount: response.page.totalPages,
        pageSize: response.page.size,
        totalElements: response.page.totalElements,
    };
};
